import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReduxSelector } from "@hooks/useReduxSelector";
import {
  SvgStatSalary,
  SvgStatHoliday,
  SvgStatEmployees,
  SvgStatPersonnalComposition,
  SvgStatPersonnalRepartition,
  SvgStatHealth,
  SvgStatunavailability,
  SvgSectionEnterpriseAdditionalInfo,
} from "@routes/IconeSvg";
import "@css/pages/employer/statistique.css";
import BigDisclosure from "@components/disclosure/BigDisclosure";
import AgeGraph from "./AgeGraph";
import AgeCategories from "./AgeGraph/AgeCategories";
import AntiquityGraph from "./AntiquityGraph";
import CityGraph from "./CityGraph";
import CongeGraph from "./CongeGraph";
import CountriesGraph from "./CountriesGraph";
import Allergy from "./Health/Allergies";
import FoodPreferences from "./Health/FoodPreferences";
import LanguagesGraph from "./LanguagesGraph";
import LocationsGraph from "./LocationsGraph";
import ParityGraph from "./ParityGraph";
import PostGraph from "./PostGraph";
import RotationGraph from "./RotationGraph";
import SalaryEvolutionGraph from "./SalaryEvolution";
import SalaryGraph from "./SalaryGraph";
import TypeDemission from "./Unavalaibility/TypeDemission";
import UnavailabilityGraph from "./UnavailabilityGraph";
import TemporalStatistics from "./TemporalStatistics";
import EmptyData from "@components/tailwindComponents/EmptyData";
import ThreeDotsLoader from "@components/loader/Loading";
import DateRangePickerDropdown from "@components/dateRange/DropdownDateRange";
import moment from "moment-timezone";
import useUnavalaibility from "@pages/shared/employeFormNew/JobAndSalary/SharedUnavailability/helpers/_functions";
import { UNAVAILABILITY_COLOR } from "@helpers/_constants";
import { setSimpleBackgroundColors } from "@helpers/_functions";
import { useDispatch } from "react-redux";
import { unavailabilityActions, employeStatisticsActions, typeDemissionActions } from "@store/actions";
import { NOT_INFORMED, COLOR_NO_INFORMATION } from "@helpers/_constants";
import { forEach } from "lodash";

export default function Graphs({ avalaibleSections, ...props }) {
  const reduxDispatch = useDispatch();
  const {
    typeUnavailabilityList,
    unavailabilityPlaceList,
    unavailabilityMainCauseList,
    unavailabilityGravityList,
    unavailabilityInjuryNatureList,
    unavailabilityBodyLocationList,
  } = useUnavalaibility();

  const [selectedPeriode, setSelectedPeriode] = useState({
    startDate: moment().subtract(11, "months").startOf("month").toDate(),
    endDate: moment().endOf("month").toDate()
  })
  const startDate = useReduxSelector(({ enterpriseEmployeData }) => enterpriseEmployeData.employeStatistics.startFilter);
  const endDate = useReduxSelector(({ enterpriseEmployeData }) => enterpriseEmployeData.employeStatistics.endFilter);
  const holidayList = useReduxSelector(({ settings }) => settings.holidayName?.list || []);
  const addionalInfoList = useReduxSelector(({ settings }) => settings.additionalInformation?.list || []);
  const useAdditionalInfo = useReduxSelector(({ settings }) => settings?.enterprise?.useAdditionalInfo) || false;
  const typeDemissionList = useReduxSelector(({ onboarding }) => onboarding.typeDemission?.list || []);
  const hasPersonelMovementsSection = useMemo(
    () => avalaibleSections?.some(
      (item) => item === "statistiquesBuildSectionPersonelMovements"
    ),
    [avalaibleSections]
  );

  useEffect(() => {
    reduxDispatch(unavailabilityActions.loadData);

    const { startDate: start, endDate: end } = selectedPeriode;
    if (start && end && startDate && endDate && (moment(start).format("YYYY-MM-DD") !== startDate || moment(end).format("YYYY-MM-DD") !== endDate)) {
      setSelectedPeriode({ startDate: moment(startDate, "YYYY-MM-DD").toDate(), endDate: moment(endDate, "YYYY-MM-DD").toDate() });
    };
  }, []);

  useEffect(() => {
    if (hasPersonelMovementsSection) {
      reduxDispatch(typeDemissionActions.loadData);
    }
  }, [hasPersonelMovementsSection, reduxDispatch]);

  const employeStatistics = useReduxSelector(
    ({ enterpriseEmployeData }) => enterpriseEmployeData.employeStatistics.data
  );
  const isLoadingUnavailability = useReduxSelector(
    ({ enterpriseEmployeData: { employeStatistics } }) =>
      Boolean(employeStatistics.isLoading || employeStatistics.isLoadingUnavailability)
  );
  const isLoadingComposition = useReduxSelector(
    ({ enterpriseEmployeData: { employeStatistics } }) =>
      Boolean(employeStatistics.isLoading || employeStatistics.isLoadingComposition)
  );
  const employePagination = useReduxSelector(
    ({ enterpriseEmployeData }) =>
      enterpriseEmployeData.employeStatistics.pagination
  );
  const selectedCompositionPeriod = useMemo(() => ({
    startDate: employePagination.compositionStartFilter
      ? moment(employePagination.compositionStartFilter, "YYYY-MM-DD").toDate()
      : moment().startOf("month").toDate(),
    endDate: employePagination.compositionEndFilter
      ? moment(employePagination.compositionEndFilter, "YYYY-MM-DD").toDate()
      : moment().endOf("month").toDate()
  }), [employePagination.compositionStartFilter, employePagination.compositionEndFilter]);
  const salaryStatistics = useReduxSelector(
    ({ enterpriseEmployeData }) => enterpriseEmployeData.salaryStatistics
  );
  const salaryEvolutions = useReduxSelector(
    ({ enterpriseEmployeData }) => enterpriseEmployeData.salaryEvolutions
  );
  const { t: translate, i18n } = useTranslation("pages.employés.statistics");
  const _validationAgeCategory = Boolean(
    employeStatistics?.agesCategories?.graphData?.find(
      (_category) => _category?.count > 0
    )
  );

  const _validationUnavailability = useMemo(() => {
    return Boolean(employeStatistics?.unavailabilities?.length)
  }, [employeStatistics?.unavailabilities?.length]);

  const demissionReasonData = useMemo(() => {
    if (employeStatistics?.demissionReason?.length) {
      return employeStatistics.demissionReason;
    }

    if (!typeDemissionList?.length) {
      return [];
    }

    return setSimpleBackgroundColors(
      typeDemissionList || [],
      UNAVAILABILITY_COLOR,
      "type"
    ).map((item) => ({
      ...item,
      count: 0,
      employeCount: 0,
      percentValue: 0
    }));
  }, [employeStatistics?.demissionReason, typeDemissionList]);

  const _validationDemissionType = Boolean(demissionReasonData?.length);

  const unavailabilityType = useMemo(() => {
    return setSimpleBackgroundColors(typeUnavailabilityList || [], UNAVAILABILITY_COLOR, "type");
  }, [typeUnavailabilityList]);

  const unavailabilityPlace = useMemo(() => {
    return setSimpleBackgroundColors(unavailabilityPlaceList || [], UNAVAILABILITY_COLOR, "place");
  }, [unavailabilityPlaceList]);

  const unavailabilityMainCause = useMemo(() => {
    return setSimpleBackgroundColors(unavailabilityMainCauseList || [], UNAVAILABILITY_COLOR, "mainCause");
  }, [unavailabilityMainCauseList]);

  const unavailabilityGravity = useMemo(() => {
    return setSimpleBackgroundColors(unavailabilityGravityList || [], UNAVAILABILITY_COLOR, "gravity");
  }, [unavailabilityGravityList]);

  const unavailabilityInjuryNature = useMemo(() => {
    return setSimpleBackgroundColors(unavailabilityInjuryNatureList || [], UNAVAILABILITY_COLOR, "injuryNature");
  }, [unavailabilityInjuryNatureList]);

  const unavailabilityBodyLocation = useMemo(() => {
    return setSimpleBackgroundColors(unavailabilityBodyLocationList || [], UNAVAILABILITY_COLOR, "bodyLocation");
  }, [unavailabilityBodyLocationList]);

  const workStopTitle = [
    { _id: "Oui", workStop: "Oui", backgroundColor: "#57A0E5" },
    { _id: "Non", workStop: "Non", backgroundColor: "#8DB29B" },
    { _id: "Non spécifié", workStop: NOT_INFORMED, backgroundColor: COLOR_NO_INFORMATION }
  ];

  const wastedTimeTitle = [
    { _id: "Oui", wastedTime: "Oui", backgroundColor: "#57A0E5" },
    { _id: "Non", wastedTime: "Non", backgroundColor: "#8DB29B" },
    { _id: "Non spécifié", wastedTime: NOT_INFORMED, backgroundColor: COLOR_NO_INFORMATION }
  ];

  function onChangePeriode(data) {
    const startDate = data[0].startDate;
    const endDate = data[0].endDate;
    const { startDate: start, endDate: end } = selectedPeriode;
    if (!moment(startDate).isSame(start, "day") || !moment(endDate).isSame(end, "day")) {
      setSelectedPeriode({
        startDate,
        endDate
      });
      reduxDispatch(employeStatisticsActions.getIndisponibility({ startFilter: moment(startDate).format("YYYY-MM-DD"), endFilter: moment(endDate).format("YYYY-MM-DD") }));
    }
  }

  function onChangeCompositionPeriod(data) {
    const { startDate, endDate } = data?.[0] || {};
    if (!startDate || !endDate || !moment(startDate).isValid() ||
        !moment(endDate).isValid() || moment(startDate).isAfter(endDate, "day")) return;
    if (moment(startDate).isSame(selectedCompositionPeriod.startDate, "day") &&
        moment(endDate).isSame(selectedCompositionPeriod.endDate, "day")) return;
    reduxDispatch(employeStatisticsActions.filterData({ compositionOnly: true, pagination: {
      age: 0,
      compositionStartFilter: moment(startDate).format("YYYY-MM-DD"),
      compositionEndFilter: moment(endDate).format("YYYY-MM-DD")
    } }));
  }

  const additionalInfoDatas = useMemo(() => {
    const nbEmployees = employeStatistics?.nbEmployees || 0;
    const listInfos = addionalInfoList?.map((info) => {
      let data = employeStatistics?.additionInformations?.find((item) => item._id === info._id);
      if (data) {
        let count = 0;
        forEach(data?.additionalInfo, (info) => count += info.count);
        data = {
          ...data,
          additionalInfo: [
            ...data.additionalInfo.map((info) => ({
              ...info,
              additional: Math.round(info.count * 10000 / nbEmployees) / 100
            })),
            ...(count < nbEmployees ? [{
              name: NOT_INFORMED,
              count: nbEmployees - count,
              backgroundColor: COLOR_NO_INFORMATION,
              additional: Math.round((nbEmployees - count) * 10000 / nbEmployees) / 100
            }] : [])
          ]
        }

      } else {
        data = {
          additionalInfo: [{ name: NOT_INFORMED, count: nbEmployees, backgroundColor: COLOR_NO_INFORMATION, additional: 100 }]
        }
      }
      return { ...info, ...data };
    });

    return listInfos;
  }, [addionalInfoList, employeStatistics?.additionInformations]);

  return (
    <div className="arh-flex arh-flex-col arh-space-y-2">
      {avalaibleSections?.some(
        (item) => item === "statistiquesBuildSectionSalaryAndLengthOfService"
      ) ? (
        <BigDisclosure
          titleIcon={SvgStatSalary()}
          title={translate("Salaires et ancienneté")}
          classParent="arh-bg-white"
          paddingRight="arh-px-9"
        >
          <div className="arh-flex arh-flex-col arh-space-y-2 arh-p-4 arh-px-9">
            <SalaryGraph
              salaryStatistics={salaryStatistics}
              setSelectedDevise={props.setSelectedDevise}
            />
            {/* Anciennete */}
            <AntiquityGraph
              _lists={employeStatistics?.antiquity?.list?.docs || []}
              details={employeStatistics?.antiquity?.details}
              average={employeStatistics?.antiquity?.list?.average}
            />
            {/* Evolution salariale */}
            <SalaryEvolutionGraph
              salaryEvolutions={salaryEvolutions}
              applyClicked={props.applyClicked}
              setSelectedYearEvolution={props.setSelectedYearEvolution}
              setSelectedDeviseEvolution={props.setSelectedDeviseEvolution}
              setSelectedYearsEvolution={props.setSelectedYearsEvolution}
              deviseList={props?.deviseList}
            />
          </div>
        </BigDisclosure>
      ) : (
        ""
      )}
      {avalaibleSections?.some(
        (item) => item === "statistiquesBuildSectionHolidays"
      ) ? (
        <BigDisclosure
          titleIcon={SvgStatHoliday()}
          title={translate("Vacances, absences et indisponibilités")}
          classParent="arh-bg-white"
          paddingRight="arh-px-9"
        >
          <div className="arh-flex arh-flex-col arh-space-y-2 arh-p-4 arh-px-9">
            {/* Prise de congé */}
            <CongeGraph conges={employeStatistics?.conges || []} holidayList={holidayList} />
          </div>
        </BigDisclosure>
      ) : (
        ""
      )}
      {hasPersonelMovementsSection ? (
        <BigDisclosure
          titleIcon={SvgStatEmployees()}
          title={translate("Mouvements de personnel")}
          classParent="arh-bg-white"
          paddingRight="arh-px-9"
        >
          <div className="arh-flex arh-flex-col arh-space-y-2 arh-p-4 arh-px-9">
            <RotationGraph
              rotation={employeStatistics?.rotation}
              rotationYear={employePagination.rotation}
            />
            {_validationDemissionType && (
              <div className="arh-grid arh-grid-cols-2 arh-gap-4 desktop-middle:arh-grid-cols-1">
                <TypeDemission
                  data={demissionReasonData}
                />
              </div>
            )}
          </div>
        </BigDisclosure>
      ) : (
        ""
      )}
      {avalaibleSections?.some(
        (item) => item === "statistiquesBuildSectionUnavalaibilityHealthAndSecurity"
      ) ? (
        <BigDisclosure
          titleIcon={SvgStatunavailability()}
          title={translate("Indisponibilités, santé et sécurité")}
          classParent="arh-bg-white"
          paddingRight="arh-px-9"
        >
          <div className="dropdown_content_hour items_filter datepicker_modals_parcours datePicker_posteAvailable blackSvgColor arh-max-w-72 arh-pl-8 arh-pb-8">
            <div className=" dateRange_green position_relative DateRangePicker_customisation DateRangePicker_customisation--green">
              <DateRangePickerDropdown
                key="date_range_periode"
                classParent="arhDatePicker"
                className="input_simple"
                dateSelected={selectedPeriode}
                textInput="Période d'indisponibilité"
                SaveCloseRange={onChangePeriode}
                disabled={isLoadingUnavailability}
                showResetFilter={false}
                classSwitch="c-switch-pill-checked--green--color--to--left"
                classBackground="green"
                isInForeground={true}
                marginTop={-10}
                customClass="arh-bottom-[3.125rem] !arh-top-auto"
              />
            </div>
          </div>
          {
            isLoadingUnavailability ?
              <ThreeDotsLoader
                color="#73C7B7"
                height="100"
                width="100"
              /> :
              !_validationUnavailability ? <div className="arh-pb-8"><EmptyData>{translate("Aucune indisponibilité en cours")}</EmptyData></div> :
                <div className="arh-grid arh-grid-cols-2 arh-gap-4 desktop-middle:arh-grid-cols-1">
                  <UnavailabilityGraph
                    categories={unavailabilityType}
                    unavailabilities={employeStatistics?.unavailabilities || []}
                    attribut="type"
                    title={translate("Type d'indisponibilité")}
                  />
                  <TemporalStatistics
                    backgroundColor="#3B82F6"
                    borderColor="#8CADC5"
                    text={translate("Date de l'événement initial")}
                    datas={employeStatistics?.unavailabilityInitialDateEvents}
                    yearTitle={translate("Année")}
                    translate={translate}
                  />
                  <UnavailabilityGraph
                    categories={unavailabilityPlace}
                    unavailabilities={employeStatistics?.unavailabilities || []}
                    attribut="place"
                    title={translate("Lieu")}
                  />
                  <UnavailabilityGraph
                    categories={unavailabilityMainCause}
                    unavailabilities={employeStatistics?.unavailabilities || []}
                    attribut="mainCause"
                    title={translate("Cause principale")}
                  />
                  <UnavailabilityGraph
                    categories={unavailabilityGravity}
                    unavailabilities={employeStatistics?.unavailabilities || []}
                    attribut="gravity"
                    title={translate("Gravité")}
                  />
                  <UnavailabilityGraph
                    categories={unavailabilityInjuryNature}
                    unavailabilities={employeStatistics?.unavailabilities || []}
                    attribut="injuryNature"
                    title={translate("Nature de la blessure/maladie")}
                  />
                  <UnavailabilityGraph
                    categories={unavailabilityBodyLocation}
                    unavailabilities={employeStatistics?.unavailabilities || []}
                    attribut="bodyLocation"
                    title={translate("Emplacement sur le corps")}
                  />
                  <UnavailabilityGraph
                    categories={workStopTitle}
                    unavailabilities={employeStatistics?.unavailabilities || []}
                    attribut="workStop"
                    title={translate("Arrêt de travail")}
                  />
                  <UnavailabilityGraph
                    categories={wastedTimeTitle}
                    unavailabilities={employeStatistics?.unavailabilities || []}
                    attribut="wastedTime"
                    title={translate("Perte de temps")}
                  />
                </div>
          }
        </BigDisclosure>) : ""}
      {avalaibleSections?.some(
        (item) => item === "statistiquesBuildSectionPersonelComposition"
      ) ? (
        <BigDisclosure
          titleIcon={SvgStatPersonnalComposition()}
          title={translate("Composition du personnel")}
          classParent="arh-bg-white"
          paddingRight="arh-px-9"
        >
          <div className="dropdown_content_hour items_filter datepicker_modals_parcours datePicker_posteAvailable blackSvgColor arh-max-w-72 arh-pl-8 arh-pb-8">
            <div className="dateRange_green position_relative DateRangePicker_customisation DateRangePicker_customisation--green">
              <DateRangePickerDropdown
                key="date_range_composition"
                classParent="arhDatePicker"
                className="input_simple"
                dateSelected={selectedCompositionPeriod}
                textInput="Période"
                SaveCloseRange={onChangeCompositionPeriod}
                disabled={isLoadingComposition}
                showResetFilter={false}
                classSwitch="c-switch-pill-checked--green--color--to--left"
                classBackground="green"
                isInForeground={true}
                marginTop={-10}
                customClass="arh-bottom-[3.125rem] !arh-top-auto"
              />
            </div>
          </div>
          {isLoadingComposition ? (
            <ThreeDotsLoader color="#73C7B7" height="100" width="100" />
          ) : <div className="arh-flex arh-flex-col arh-space-y-2 arh-p-4 arh-px-9">
            {/* Ages */}
            <AgeGraph
              ages={employeStatistics?.ages?.list || []}
              details={employeStatistics?.ages?.details}
            />
            <div
              className={
                _validationAgeCategory
                  ? "arh-grid arh-grid-cols-2 arh-gap-4 desktop-middle:arh-grid-cols-1"
                  : "arh-w-full"
              }
            >
              {/* Parite */}
              {_validationAgeCategory && (
                <AgeCategories
                  categories={
                    employeStatistics?.agesCategories?.graphData || []
                  }
                />
              )}
              <ParityGraph parity={employeStatistics?.parity || []} title={translate("Parité")} />
            </div>
          </div>}
        </BigDisclosure>
      ) : (
        ""
      )}
      {avalaibleSections?.some(
        (item) => item === "statistiquesBuildSectionPersonelDistribution"
      ) ? (
        <BigDisclosure
          titleIcon={SvgStatPersonnalRepartition()}
          title={translate("Répartition du personnel")}
          classParent="arh-bg-white"
          paddingRight="arh-px-9"
        >
          <div className="arh-grid arh-grid-cols-2 arh-gap-4 desktop-middle:arh-grid-cols-1 arh-p-4 arh-px-9">
            <PostGraph
              employeesPerPosts={employeStatistics?.getByPosts || []}
            />
            <LocationsGraph
              employeesPerLocations={employeStatistics?.getByLocations || []}
            />
            <CityGraph employeesPerCity={employeStatistics?.getByTown || []} />
            <CountriesGraph
              employeesPerCountries={employeStatistics?.getByCountries || []}
            />
            <LanguagesGraph
              employeesPerLanguages={employeStatistics?.getByLanguages || []}
            />
          </div>
        </BigDisclosure>
      ) : (
        ""
      )}
      {avalaibleSections?.some(
        (item) => item === "statistiquesBuildSectionHealthAndFoodPreferences"
      ) ? (
        <BigDisclosure
          titleIcon={SvgStatHealth()}
          title={translate("Santé - Allergies et préférences alimentaires")}
          classParent="arh-bg-white"
          paddingRight="arh-px-9"
        >
          <div className="arh-grid arh-grid-cols-2 arh-gap-4 desktop-middle:arh-grid-cols-1 arh-p-4 arh-px-9">
            <Allergy data={employeStatistics?.allergies?.graphData || []} />
            <FoodPreferences
              data={employeStatistics?.foodPreferences?.graphData || []}
            />
          </div>
        </BigDisclosure>
      ) : (
        ""
      )}
      {(avalaibleSections?.some(
        (item) => item === "statistiquesBuildSectionAdditionalInformation"
      ) && useAdditionalInfo) ? (
        <BigDisclosure
          titleIcon={SvgSectionEnterpriseAdditionalInfo()}
          title={translate("Informations complémentaires")}
          classParent="arh-bg-white"
          paddingRight="arh-px-9"
        >
          {
            additionalInfoDatas?.length === 0 ? <EmptyData>{translate("Aucune information complémentaires à afficher")}</EmptyData> :
              <div className="arh-grid arh-grid-cols-2 arh-gap-4 desktop-middle:arh-grid-cols-1 arh-p-4 arh-px-9">
                {
                  additionalInfoDatas.map((item) => (
                    <ParityGraph
                      parity={item.additionalInfo}
                      title={i18n.language === "fr" ? item.designationFr : item.designationEn}
                      key={item._id}
                    />
                  ))
                }
              </div>
          }
        </BigDisclosure>
      ) : (
        ""
      )}
    </div>
  );
}
